import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ArrowRight,
  BadgePlus,
  Building2,
  CalendarClock,
  CheckCircle,
  GitBranch,
  Globe,
  Handshake,
  Loader2,
  MessageSquare,
  Bot,
  Settings,
  Users,
  Activity,
  Wallet,
  Wifi,
  Zap,
} from 'lucide-react';
import { orgsApi } from '@/api/orgs';
import { orgUsersApi } from '@/api/orgUsers';
import type { ResendChannel } from '@/api/resendInvite';
import ResendInviteButtons from '@/components/ResendInviteButtons';
import { Badge, Button, Loading, Modal } from '@/components/ui';
import { formatDate } from '@/utils/date';
import { cn, hebrewLabel, ORG_STATUS_LABELS } from '@/utils/helpers';
import type { AdminSessionMode } from '@/types';
import OrgInfoTab from './tabs/OrgInfoTab';
import OrgBranchesTab from './tabs/OrgBranchesTab';
import OrgIntegrationsTab from './tabs/OrgIntegrationsTab';
import OrgBillingTab from './tabs/OrgBillingTab';
import OrgMessagesTab from './tabs/OrgMessagesTab';
import OrgWaLogTab from './tabs/OrgWaLogTab';
import OrgAffiliatesTab from './tabs/OrgAffiliatesTab';
import OrgSubscriptionsTab from './tabs/OrgSubscriptionsTab';
import OrgAddonsTab from './tabs/OrgAddonsTab';
import OrgUsersTab from './tabs/OrgUsersTab';
import LicenseSummaryCard from './LicenseSummaryCard';
import OpsMobileNav from '@/components/OpsMobileNav';
import OrgUsageTab from './tabs/OrgUsageTab';
import OrgIntentsTab from './tabs/OrgIntentsTab';
import OrgMeetTranscriptionTab from './tabs/OrgMeetTranscriptionTab';

const TABS = [
  { id: 'info', label: 'Info', icon: Building2 },
  { id: 'subscriptions', label: 'Subscriptions', icon: Wallet },
  { id: 'addons', label: 'Add-ons', icon: BadgePlus },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'usage', label: 'Usage', icon: Activity },
  { id: 'meet-transcription', label: 'תמלול פגישות', icon: MessageSquare },
  { id: 'branches', label: 'Branches', icon: GitBranch },
  { id: 'billing', label: 'Billing', icon: Wallet },
  { id: 'integrations', label: 'Integrations', icon: Wifi },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'wa-log', label: 'תיעוד שיחות', icon: MessageSquare },
  { id: 'intents', label: 'Intents', icon: Bot },
  { id: 'affiliates', label: 'Affiliates', icon: Handshake },
  { id: 'settings', label: 'Settings', icon: Settings },
] as const;

type TabId = typeof TABS[number]['id'];

function isTabId(value: string | null): value is TabId {
  return !!value && TABS.some((tab) => tab.id === value);
}

const STATUS_BADGE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  trial: 'warning',
  suspended: 'danger',
  cancelled: 'neutral',
};

/** archived_at + 1 year — the date the auto-purge job will permanently delete the org. */
function scheduledDeletionDate(archivedAt: string): string {
  const d = new Date(archivedAt);
  d.setFullYear(d.getFullYear() + 1);
  return formatDate(d.toISOString());
}

function resetImpersonationState(
  close: () => void,
  setSessionMode: (mode: AdminSessionMode) => void,
  setReason: (value: string) => void,
) {
  close();
  setSessionMode('view');
  setReason('');
}

export default function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState<TabId>(isTabId(tabFromUrl) ? tabFromUrl : 'info');

  useEffect(() => {
    if (isTabId(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTabState(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);

  const setActiveTab = (tab: TabId) => {
    setActiveTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'info') next.delete('tab');
      else next.set('tab', tab);
      return next;
    }, { replace: true });
  };
  const [settingUpPortal, setSettingUpPortal] = useState(false);
  const [portalStatus, setPortalStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [portalError, setPortalError] = useState('');
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  const [sessionMode, setSessionMode] = useState<AdminSessionMode>('view');
  const [reason, setReason] = useState('');
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: org, isLoading } = useQuery({
    queryKey: ['admin-org', id],
    queryFn: () => orgsApi.get(id!),
    enabled: !!id,
  });

  const archiveMutation = useMutation({
    mutationFn: () => orgsApi.archive(id!),
    onSuccess: () => {
      toast.success('Organization archived — hidden from the hub for all members.');
      setArchiveConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['admin-org', id] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to archive'),
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => orgsApi.unarchive(id!),
    onSuccess: () => {
      toast.success('Organization restored.');
      void queryClient.invalidateQueries({ queryKey: ['admin-org', id] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to unarchive'),
  });

  const handleSetupPortal = async () => {
    if (!org) return;
    setSettingUpPortal(true);
    setPortalStatus('idle');

    try {
      const result = await orgsApi.setupPortalDomain(org.id, org.slug);
      if (result.success) {
        setPortalStatus('success');
      } else {
        setPortalStatus('error');
        setPortalError(result.error || 'Unknown error');
      }
    } catch (err) {
      setPortalStatus('error');
      setPortalError(err instanceof Error ? err.message : 'Failed to setup portal domain');
    } finally {
      setSettingUpPortal(false);
    }
  };

  const handleResend = async (channel: ResendChannel) => {
    if (!id) return;

    try {
      const { sent_to } = await orgsApi.resendOwnerInvite(id, channel);
      toast.success(`${channel === 'email' ? 'Email' : 'WhatsApp'} sent to ${sent_to}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to resend invite');
    }
  };

  const impersonateMutation = useMutation({
    mutationFn: async () => {
      if (!org) throw new Error('Organization not loaded');
      if (sessionMode === 'edit' && !reason.trim()) {
        throw new Error('Reason is required for edit mode');
      }
      return orgUsersApi.impersonateAsOwner(org.id, sessionMode, reason.trim() || undefined);
    },
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener,noreferrer');
      resetImpersonationState(() => setImpersonationOpen(false), setSessionMode, setReason);
    },
  });

  if (isLoading) return <Loading text="Loading organization..." />;
  if (!org) return <div className="py-20 text-center text-ink-secondary">Organization not found</div>;

  return (
    <div dir="rtl">
      <div className="mb-5"><OpsMobileNav /></div>
      <div className="mb-6 flex items-center gap-2 text-sm text-ink-muted">
        <Link to="/orgs" className="flex items-center gap-1 transition-colors hover:text-gold">
          <ArrowRight className="h-3.5 w-3.5" />
          Organizations
        </Link>
        <span>/</span>
        <span className="text-ink-primary">{org.name}</span>
      </div>

      <div className="mb-8 flex flex-wrap items-start gap-4">
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gold/10 text-xl font-black text-gold">
          {org.name.charAt(0)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-ink-primary">{org.name}</h1>
            <Badge variant={STATUS_BADGE[org.status ?? ''] ?? 'neutral'}>
              {hebrewLabel(org.status ?? '', ORG_STATUS_LABELS)}
            </Badge>
            {org.archived_at ? (
              <Badge variant="neutral">Archived</Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-ink-secondary num">{org.email}</p>
        </div>

        <div className="flex shrink-0 items-start gap-2">
          <ResendInviteButtons
            hasEmail={!!org.email}
            hasPhone={!!org.phone}
            onResend={handleResend}
            size="md"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => setImpersonationOpen(true)}
            title={
              org.allow_owner_impersonation === false
                ? 'Customer has not granted admin access'
                : 'Open a 15-minute owner session in Hub'
            }
            className="gap-2"
          >
            <Zap className="h-4 w-4" />
            Enter as Owner
          </Button>
          {org.archived_at ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void unarchiveMutation.mutateAsync()}
              loading={unarchiveMutation.isPending}
              title="Restore this org and make it visible again to its members"
              className="gap-2"
            >
              <ArchiveRestore className="h-4 w-4" />
              Unarchive
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setArchiveConfirmOpen(true)}
              title="Hide this org from the hub for all members (reversible)"
              className="gap-2"
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>
          )}
        </div>
      </div>

      {org.archived_at ? (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
          <CalendarClock className="h-4 w-4 flex-shrink-0" />
          <span>
            Archived on <span className="num">{formatDate(org.archived_at)}</span>. Hidden from the hub for all members.
            {' '}Scheduled for permanent deletion on{' '}
            <span className="num font-semibold">{scheduledDeletionDate(org.archived_at)}</span>{' '}
            (1 year after archiving).
          </span>
        </div>
      ) : null}

      <LicenseSummaryCard
        orgId={org.id}
        manageHref={`/migration/run/${org.id}`}
      />

      <div className="mb-5 grid grid-cols-2 gap-2 lg:hidden">
        <Button type="button" variant="secondary" onClick={() => setActiveTab('subscriptions')}>רישוי ומכסות</Button>
        <Button type="button" variant="secondary" onClick={() => setActiveTab('users')}>ניהול משתמשים</Button>
        {org.archived_at ? (
          <Button type="button" variant="secondary" onClick={() => void unarchiveMutation.mutateAsync()} loading={unarchiveMutation.isPending}>שחזור ארגון</Button>
        ) : (
          <Button type="button" variant="secondary" onClick={() => setArchiveConfirmOpen(true)}>הקפאת ארגון</Button>
        )}
        <Button type="button" variant="secondary" onClick={() => setActiveTab('addons')}>תוספות ושדרוג</Button>
      </div>

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-navy-border">
        {TABS.map((tab) => {
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap border-b-2 -mb-px px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-gold text-gold'
                  : 'border-transparent text-ink-secondary hover:text-ink-primary'
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'info' && <OrgInfoTab org={org} />}
      {activeTab === 'subscriptions' && <OrgSubscriptionsTab orgId={org.id} />}
      {activeTab === 'addons' && <OrgAddonsTab orgId={org.id} orgName={org.name} />}
      {activeTab === 'users' && <OrgUsersTab orgId={org.id} orgName={org.name} />}
      {activeTab === 'usage' && <OrgUsageTab orgId={org.id} />}
      {activeTab === 'meet-transcription' && <OrgMeetTranscriptionTab key={org.id} orgId={org.id} />}
      {activeTab === 'branches' && <OrgBranchesTab org={org} />}
      {activeTab === 'billing' && <OrgBillingTab orgId={org.id} />}
      {activeTab === 'integrations' && <OrgIntegrationsTab orgId={org.id} />}
      {activeTab === 'messages' && <OrgMessagesTab orgId={org.id} />}
      {activeTab === 'wa-log' && <OrgWaLogTab orgId={org.id} />}
      {activeTab === 'intents' && <OrgIntentsTab orgId={org.id} />}
      {activeTab === 'affiliates' && <OrgAffiliatesTab org={org} />}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="mb-4 flex items-center gap-3">
              <Globe className="h-5 w-5 text-indigo-400" />
              <h3 className="text-lg font-semibold text-ink-primary">Client Portal Domain</h3>
            </div>

            <p className="mb-2 text-sm text-ink-secondary">
              Portal URL:{' '}
              <span className="font-mono text-indigo-400">{org.slug}.client.jumpstart.co.il</span>
            </p>

            {portalStatus === 'success' && (
              <div className="mb-4 flex items-center gap-2 text-sm text-teal-400">
                <CheckCircle className="h-4 w-4" />
                <span>Domain configured successfully</span>
              </div>
            )}

            {portalStatus === 'error' && (
              <div className="mb-4 flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span>{portalError}</span>
              </div>
            )}

            <button
              type="button"
              onClick={handleSetupPortal}
              disabled={settingUpPortal}
              className="btn-primary flex items-center gap-2"
            >
              {settingUpPortal ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4" />
                  Setup Portal Domain
                </>
              )}
            </button>
          </div>

          <div className="card p-6 text-center text-sm text-ink-muted">
            More organization settings - coming soon
          </div>
        </div>
      )}

      <Modal
        isOpen={impersonationOpen}
        onClose={() => resetImpersonationState(() => setImpersonationOpen(false), setSessionMode, setReason)}
        title="Enter as Owner"
        footer={(
          <>
            <Button
              variant="secondary"
              onClick={() => resetImpersonationState(() => setImpersonationOpen(false), setSessionMode, setReason)}
              disabled={impersonateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void impersonateMutation.mutateAsync()}
              loading={impersonateMutation.isPending}
              disabled={sessionMode === 'edit' && !reason.trim()}
            >
              Open session
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-navy-border bg-navy/40 p-3 text-sm text-ink-secondary">
            This opens a fresh owner session in Hub for 15 minutes. The action is fully audited.
          </div>

          {org.allow_owner_impersonation === false ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
              Customer has not granted admin access. Phase 1 still allows the session, but the flag remains visible.
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-sm font-medium text-ink-primary">Mode</p>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-navy-border p-3">
                <input
                  type="radio"
                  name="session-mode"
                  checked={sessionMode === 'view'}
                  onChange={() => setSessionMode('view')}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-ink-primary">View-only</p>
                  <p className="text-xs text-ink-secondary">Default support mode with no edit intent.</p>
                </div>
              </label>

              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-navy-border p-3">
                <input
                  type="radio"
                  name="session-mode"
                  checked={sessionMode === 'edit'}
                  onChange={() => setSessionMode('edit')}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-ink-primary">Edit</p>
                  <p className="text-xs text-ink-secondary">Requires a reason and is logged in the audit trail.</p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink-primary">Reason</label>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={sessionMode === 'edit' ? 'Required for edit mode' : 'Optional note for the audit log'}
              rows={4}
              className="w-full rounded-lg border border-navy-border bg-navy-card px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
            {sessionMode === 'edit' ? (
              <p className="mt-1 text-xs text-ink-secondary">Edit mode requires a written reason before opening the session.</p>
            ) : null}
          </div>

          {impersonateMutation.error ? (
            <p className="text-sm text-danger">{(impersonateMutation.error as Error).message}</p>
          ) : null}
        </div>
      </Modal>

      <Modal
        isOpen={archiveConfirmOpen}
        onClose={() => setArchiveConfirmOpen(false)}
        title="Archive organization"
        footer={(
          <>
            <Button
              variant="secondary"
              onClick={() => setArchiveConfirmOpen(false)}
              disabled={archiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void archiveMutation.mutateAsync()}
              loading={archiveMutation.isPending}
            >
              Archive
            </Button>
          </>
        )}
      >
        <div className="space-y-3 text-sm text-ink-secondary">
          <p>
            Archiving <span className="font-medium text-ink-primary">{org.name}</span> hides it from the hub
            switcher and app for all members immediately. No data is deleted.
          </p>
          <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-300">
            Archived orgs are permanently deleted 1 year after archiving by the auto-purge job.
            You can unarchive at any time before then to restore access.
          </p>
        </div>
      </Modal>
    </div>
  );
}
